import React, { useEffect, useMemo, useState } from 'react'
import BuildingPassportList from 'components/BuildingPassportList'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import LayersRoundedIcon from '@mui/icons-material/LayersRounded'
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { listPortfolioEstates, createPortfolioEstate } from 'services/estateService'
import { SkeletonRows } from 'components/Skeleton'
import PassportEmblem from 'components/PassportEmblem'
import SearchableSelect from 'components/SearchableSelect'
import { RAG, scoreColour, scoreBand, formatPercent } from 'features/dashboard/charts/scoreColour'
import { font, ink, line, radius, surface, transition } from 'features/dashboard/tokens'
import { DASHBOARD, remedialsViewUrl } from 'features/actions/workspace/remedialsQuery'

const ESTATE_ACCENT = '#7C5FE0'

const SORTS = [
  { value: 'name', label: 'Name (A to Z)' },
  { value: 'score', label: 'Compliance score (lowest first)' },
  { value: 'unmet', label: 'Unmet statutory (most first)' },
  { value: 'overdue', label: 'Overdue tasks (most first)' },
  { value: 'remedials', label: 'Open remedials (most first)' },
]

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

const isActive = bp => !bp.status || String(bp.status).toLowerCase() === 'active'

const needsAttention = bp => isActive(bp) && (
  (bp.compliance?.unmetStatutory || 0) > 0
  || (bp.compliance?.expired || 0) > 0
  || (bp.tasks?.overdue || 0) > 0
  || (bp.remedials?.overdue || 0) > 0
  || (bp.incidents?.open || 0) > 0
)

const sortBuildings = (list, sort) => {
  const byName = (a, b) => (a.name || '').localeCompare(b.name || '')
  const copy = [...list]
  switch (sort) {
    case 'score':
      return copy.sort((a, b) => ((a.compliance?.score ?? 101) - (b.compliance?.score ?? 101)) || byName(a, b))
    case 'unmet':
      return copy.sort((a, b) => ((b.compliance?.unmetStatutory || 0) - (a.compliance?.unmetStatutory || 0)) || byName(a, b))
    case 'overdue':
      return copy.sort((a, b) => ((b.tasks?.overdue || 0) - (a.tasks?.overdue || 0)) || byName(a, b))
    case 'remedials':
      return copy.sort((a, b) => ((b.remedials?.open || 0) - (a.remedials?.open || 0)) || byName(a, b))
    default:
      return copy.sort(byName)
  }
}

const scoreTitle = score => (score === null || score === undefined
  ? 'No compliance score yet. The share of weighted obligations satisfied could not be calculated for this building.'
  : `Compliance score ${formatPercent(score)} (${scoreBand(score)}). The share of weighted obligations satisfied, where each obligation is weighted by its criticality.`)

const groupTotals = members => members.filter(isActive).reduce((acc, m) => ({
  unmet: acc.unmet + (m.compliance?.unmetStatutory || 0),
  overdue: acc.overdue + (m.tasks?.overdue || 0),
  remedials: acc.remedials + (m.remedials?.open || 0),
}), { unmet: 0, overdue: 0, remedials: 0 })

const PortfolioBuildingsEstates = ({ portfolioId, buildingPassports = [], canEdit = false, showCompliance = false, onAddBps, onRemoveBp }) => {
  const navigate = useNavigate()
  const goRemedials = (bpId, view) => navigate(remedialsViewUrl({ bpId }, view))
  const [estates, setEstates] = useState(null)
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState('name')
  const [attentionOnly, setAttentionOnly] = useState(false)

  const load = () => {
    listPortfolioEstates(portfolioId)
      .then(res => setEstates(res?.data?.data || []))
      .catch(() => setEstates([]))
  }

  useEffect(() => { load() }, [portfolioId])

  const createEstate = async () => {
    const trimmed = name.trim()
    if (!trimmed || saving) return
    setSaving(true)
    try {
      await createPortfolioEstate(portfolioId, { name: trimmed })
      toast.success('Estate created.')
      setName('')
      setCreating(false)
      load()
    } catch (error) {
      toast.error('Could not create the estate.')
    } finally {
      setSaving(false)
    }
  }

  const bpById = useMemo(
    () => new Map(buildingPassports.map(bp => [bp.id, bp])),
    [buildingPassports]
  )

  const { estateGroups, ungrouped, ungroupedShown, shownCount, totalBuildings } = useMemo(() => {
    const q = query.trim().toLowerCase()
    const matches = bp => {
      if (attentionOnly && !needsAttention(bp)) return false
      if (!q) return true
      return [bp.name, bp.street, bp.city, bp.postalCode].filter(Boolean).some(v => String(v).toLowerCase().includes(q))
    }
    const usedIds = new Set()
    const groups = (estates || []).map(estate => {
      const members = (estate.buildingPassports || []).map(member => {
        usedIds.add(member.id)
        return bpById.get(member.id) || member
      })
      return { estate, members, shown: sortBuildings(members.filter(matches), sort) }
    })
    const loose = buildingPassports.filter(bp => !usedIds.has(bp.id))
    const looseShown = sortBuildings(loose.filter(matches), sort)
    const count = groups.reduce((n, g) => n + g.shown.length, 0) + looseShown.length
    const total = usedIds.size + loose.length
    return { estateGroups: groups, ungrouped: loose, ungroupedShown: looseShown, shownCount: count, totalBuildings: total }
  }, [estates, buildingPassports, bpById, query, sort, attentionOnly])

  if (estates === null) return <SkeletonRows rows={5} />

  const totalBps = totalBuildings
  const isEmpty = totalBps === 0 && estateGroups.length === 0
  const filtering = query.trim() !== '' || attentionOnly

  const hasDetail = bp => !!bp.compliance
  const opsEnabled = bp => !!(bp.hasTaskManagement || bp.taskManagementEnabled)

  const renderRing = bp => {
    if (!isActive(bp)) {
      return (
        <Ring $colour={scoreColour(null)} $deg={0} title='This building is dormant, so it is not counted in the compliance and work figures.' aria-label='Dormant building'>
          <RingInner>–</RingInner>
        </Ring>
      )
    }
    if (!hasDetail(bp)) {
      return (
        <Ring $colour={scoreColour(null)} $deg={0} title='No detail available for this building yet' aria-label='No detail available'>
          <RingInner>–</RingInner>
        </Ring>
      )
    }
    const tracked = bp.tracksCompliance !== false
    const score = tracked ? (bp.compliance?.score ?? null) : null
    const title = tracked ? scoreTitle(score) : 'Compliance tracking is turned off for this building.'
    return (
      <Ring $colour={scoreColour(score)} $deg={(score ?? 0) * 3.6} title={title} aria-label={title}>
        <RingInner>{tracked ? formatPercent(score) : 'Off'}</RingInner>
      </Ring>
    )
  }

  const renderMeta = bp => {
    if (!isActive(bp)) {
      return (
        <>
          <Pill $tone='medium' title='This building is dormant'>Dormant</Pill>
          <Calm>Not counted in the compliance and work figures</Calm>
        </>
      )
    }
    if (!hasDetail(bp)) return <Calm>Detail unavailable for this building</Calm>
    const c = bp.compliance || {}
    const t = bp.tasks || {}
    const r = bp.remedials || {}
    const i = bp.incidents || {}
    const tracked = bp.tracksCompliance !== false
    return (
      <>
        {showCompliance && !tracked && <Calm>Compliance tracking off</Calm>}
        {showCompliance && tracked && ((c.unmetStatutory || 0) > 0
          ? <Warning title='Statutory obligations this building does not currently meet'>⚠ {c.unmetStatutory} unmet statutory</Warning>
          : <Calm>No statutory gaps</Calm>)}
        {showCompliance && tracked && (
          <MetaDim title='Obligations satisfied, with expired evidence, and still pending'>
            {c.satisfied || 0} satisfied · {c.expired || 0} expired · {c.pending || 0} pending
          </MetaDim>
        )}
        {opsEnabled(bp) ? (
          <Pills>
            {(t.overdue || 0) > 0 && <Pill $tone='critical' title='Tasks past their deadline'>{plural(t.overdue, 'overdue task')}</Pill>}
            <Pill $tone={(t.open || 0) > 0 ? 'medium' : 'low'} title='Planned work not yet completed'>{plural(t.open || 0, 'open task')}</Pill>
            <LinkPill type='button' $tone={(r.open || 0) > 0 ? 'medium' : 'low'} title='Remedials still being dealt with. Click to see them.' onClick={e => { e.stopPropagation(); goRemedials(bp.id, 'all_open') }}>
              {plural(r.open || 0, 'open remedial')}
            </LinkPill>
            <LinkPill type='button' $tone={(r.overdue || 0) > 0 ? 'critical' : 'low'} title='Open remedials past their due date. Click to see them.' onClick={e => { e.stopPropagation(); goRemedials(bp.id, 'overdue') }}>
              {r.overdue || 0} overdue
            </LinkPill>
            <LinkPill type='button' $tone={(r.needsDecision || 0) > 0 ? 'high' : 'low'} title='Open remedials nobody has acted on yet. Click to see them.' onClick={e => { e.stopPropagation(); goRemedials(bp.id, 'needs_decision') }}>
              {r.needsDecision || 0} need{(r.needsDecision || 0) === 1 ? 's' : ''} a decision
            </LinkPill>
            <Pill $tone={(i.open || 0) > 0 ? 'high' : 'low'} title='Reported incidents still being dealt with'>{plural(i.open || 0, 'open incident')}</Pill>
          </Pills>
        ) : (
          <Calm>Task management not enabled</Calm>
        )}
      </>
    )
  }

  const linksFor = bp => [
    { label: 'Overview', onClick: () => navigate(`/bp-list/${bp.id}`) },
    ...(showCompliance && isActive(bp) ? [{ label: 'Compliance', onClick: () => navigate(`/bp-list/${bp.id}/compliance?section=programme`) }] : []),
    ...(opsEnabled(bp) && isActive(bp) ? [
      { label: 'Tasks', onClick: () => navigate(`/tasks?bp=${bp.id}&from=${bp.id}`) },
      { label: 'Remedials', onClick: () => goRemedials(bp.id, DASHBOARD) },
    ] : []),
    { label: 'Documents', onClick: () => navigate(`/bp-list/${bp.id}/other`) },
  ]

  const renderList = list => (
    <BuildingPassportList
      buildings={list}
      onRemove={canEdit && onRemoveBp ? onRemoveBp : undefined}
      removeLabel='Remove from portfolio'
      renderLead={showCompliance ? renderRing : undefined}
      renderMeta={renderMeta}
      links={linksFor}
    />
  )

  return (
    <Wrap>
      <Toolbar>
        <Summary>
          <strong>{totalBps}</strong> building{totalBps === 1 ? '' : 's'}
          {' · '}
          <strong>{estateGroups.length}</strong> estate{estateGroups.length === 1 ? '' : 's'}
        </Summary>
        {canEdit && (
          <ToolbarActions>
            {creating ? (
              <CreateRow>
                <CreateInput
                  autoFocus
                  placeholder='Estate name'
                  value={name}
                  onChange={event => setName(event.target.value)}
                  onKeyDown={event => { if (event.key === 'Enter') createEstate() }}
                />
                <PrimaryBtn type='button' disabled={saving || !name.trim()} onClick={createEstate}>
                  {saving ? 'Creating…' : 'Create estate'}
                </PrimaryBtn>
                <GhostBtn type='button' onClick={() => { setCreating(false); setName('') }}>Cancel</GhostBtn>
              </CreateRow>
            ) : (
              <>
                <SecondaryBtn type='button' onClick={() => setCreating(true)}>
                  <LayersRoundedIcon style={{ fontSize: 16 }} /> New estate
                </SecondaryBtn>
                {onAddBps && (
                  <PrimaryBtn type='button' onClick={onAddBps}>
                    <AddRoundedIcon style={{ fontSize: 16 }} /> Add Building Passports
                  </PrimaryBtn>
                )}
              </>
            )}
          </ToolbarActions>
        )}
      </Toolbar>

      {!isEmpty && (
        <Filters>
          <SearchBox>
            <SearchRoundedIcon style={{ fontSize: 18 }} aria-hidden='true' />
            <SearchInput
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder='Search buildings by name or address'
              aria-label='Search buildings by name or address'
            />
          </SearchBox>
          <SortBox>
            <SortLabel>Sort by</SortLabel>
            <SearchableSelect
              value={sort}
              onChange={next => setSort(next && typeof next === 'object' ? next.value : (next || 'name'))}
              options={SORTS}
              searchable={false}
              ariaLabel='Sort buildings'
            />
          </SortBox>
          <FilterChip
            type='button'
            $on={attentionOnly}
            aria-pressed={attentionOnly}
            onClick={() => setAttentionOnly(value => !value)}
            title='Show only buildings with unmet statutory obligations, expired evidence, overdue work or open incidents'
          >
            Needs attention only
          </FilterChip>
          <Showing>Showing {shownCount} of {totalBps}</Showing>
        </Filters>
      )}

      {isEmpty ? (
        <Empty>
          No Building Passports yet.
          {canEdit && onAddBps && ' Add some, then group them into estates to manage them together.'}
        </Empty>
      ) : (
        <Groups>
          {estateGroups.map(({ estate, members, shown }) => {
            const totals = groupTotals(members)
            if (filtering && shown.length === 0) return null
            return (
              <Division key={estate.id}>
                <DivisionHead onClick={() => navigate(`/estates/${estate.id}`)} title={`Open ${estate.name}`}>
                  <PassportEmblem kind='estate' size='xs' showLabel={false} />
                  <DivisionName>{estate.name}</DivisionName>
                  <DivisionMeta>
                    {plural(members.length, 'building')}
                    {showCompliance && (totals.unmet > 0
                      ? <> · <Warning>{totals.unmet} unmet statutory</Warning></>
                      : <> · No statutory gaps</>)}
                    {totals.overdue > 0 && <> · <Warning>{plural(totals.overdue, 'overdue task')}</Warning></>}
                    {totals.remedials > 0 && <> · {plural(totals.remedials, 'open remedial')}</>}
                  </DivisionMeta>
                  <DivisionSpacer />
                  <OpenLink>Open estate <span aria-hidden='true'>→</span></OpenLink>
                </DivisionHead>
                {members.length === 0 ? (
                  <DivisionEmpty>No Building Passports in this estate yet.</DivisionEmpty>
                ) : renderList(shown)}
              </Division>
            )
          })}

          {ungrouped.length > 0 && (!filtering || ungroupedShown.length > 0) && (
            <Division>
              {estateGroups.length > 0 && (
                <DivisionHead as='div' $ungrouped>
                  <DivisionBadge $ungrouped aria-hidden='true'><HomeWorkRoundedIcon style={{ fontSize: 16 }} /></DivisionBadge>
                  <DivisionName $ungrouped>Not in an estate</DivisionName>
                  <DivisionMeta>{plural(ungrouped.length, 'building')}</DivisionMeta>
                </DivisionHead>
              )}
              {renderList(ungroupedShown)}
            </Division>
          )}

          {filtering && shownCount === 0 && (
            <Empty>No buildings match. Clear the search or turn off the filter to see every building.</Empty>
          )}
        </Groups>
      )}
    </Wrap>
  )
}

export default PortfolioBuildingsEstates

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`

const Summary = styled.div`
  font-size: ${font.body};
  color: ${ink.soft};

  strong { color: ${ink.base}; font-weight: 800; }
`

const ToolbarActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const CreateRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const CreateInput = styled.input`
  min-width: 200px;
  padding: 8px 12px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  font: inherit;
  font-size: ${font.body};
  outline: none;

  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 15px;
  border: none;
  border-radius: ${radius.pill};
  background: ${({ theme }) => theme.colors.primary};
  color: #ffffff;
  font: inherit;
  font-size: ${font.caption};
  font-weight: 700;
  cursor: pointer;
  transition: ${transition};

  &:hover { opacity: 0.92; }
  &:disabled { opacity: 0.5; cursor: default; }
`

const SecondaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 15px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.pill};
  background: ${surface.card};
  color: ${ink.soft};
  font: inherit;
  font-size: ${font.caption};
  font-weight: 700;
  cursor: pointer;
  transition: ${transition};

  &:hover { border-color: ${ESTATE_ACCENT}; color: ${ESTATE_ACCENT}; }
`

const GhostBtn = styled.button`
  padding: 8px 10px;
  border: none;
  background: transparent;
  color: ${ink.mute};
  font: inherit;
  font-size: ${font.caption};
  font-weight: 600;
  cursor: pointer;

  &:hover { color: ${ink.base}; }
`

const Groups = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`

const Division = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const DivisionHead = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  border: 1px solid ${({ $ungrouped }) => ($ungrouped ? line.base : `${ESTATE_ACCENT}33`)};
  border-radius: ${radius.md};
  background: ${({ $ungrouped }) => ($ungrouped ? surface.wash : `${ESTATE_ACCENT}0c`)};
  cursor: ${({ $ungrouped }) => ($ungrouped ? 'default' : 'pointer')};
  font: inherit;
  transition: ${transition};

  &:hover {
    background: ${({ $ungrouped }) => ($ungrouped ? surface.wash : `${ESTATE_ACCENT}16`)};
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 8px 10px;
  }
`

const DivisionBadge = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${radius.sm};
  background: ${({ $ungrouped }) => ($ungrouped ? '#eceef1' : `${ESTATE_ACCENT}1f`)};
  color: ${({ $ungrouped }) => ($ungrouped ? ink.mute : ESTATE_ACCENT)};
`

const DivisionName = styled.span`
  min-width: 0;
  font-size: ${font.body};
  font-weight: 800;
  color: ${({ $ungrouped }) => ($ungrouped ? ink.soft : ink.base)};

  @media (max-width: 768px) {
    flex: 1 1 auto;
  }
`

const DivisionMeta = styled.span`
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${ink.mute};

  @media (max-width: 768px) {
    flex-basis: 100%;
    padding-left: 40px;
    line-height: 1.5;
  }
`

const DivisionSpacer = styled.span`
  flex: 1;

  @media (max-width: 768px) {
    display: none;
  }
`

const OpenLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border: 1px solid ${ESTATE_ACCENT}55;
  border-radius: ${radius.pill};
  background: ${ESTATE_ACCENT}0f;
  color: ${ESTATE_ACCENT};
  font-size: ${font.caption};
  font-weight: 700;
  white-space: nowrap;
  transition: ${transition};

  ${DivisionHead}:hover & { background: ${ESTATE_ACCENT}; color: #fff; }

  @media (max-width: 768px) {
    margin-left: auto;
  }
`

const DivisionEmpty = styled.div`
  padding: 8px 14px 8px 52px;
  font-size: ${font.small};
  color: ${ink.mute};
  font-style: italic;
`









const Empty = styled.div`
  padding: 32px;
  text-align: center;
  font-size: ${font.body};
  color: ${ink.mute};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  background: ${surface.wash};
`

const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const SearchBox = styled.label`
  flex: 1 1 260px;
  min-width: 200px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: 40px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.mute};

  &:focus-within { border-color: ${({ theme }) => theme.colors.secondary}; }
`

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: ${font.body};
  color: ${ink.base};

  &::placeholder { color: ${ink.mute}; }
`

const SortBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 240px;
`

const SortLabel = styled.span`
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${ink.mute};
  white-space: nowrap;
`

const FilterChip = styled.button`
  height: 40px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid ${({ $on }) => ($on ? RAG.high.fg : line.strong)};
  background: ${({ $on }) => ($on ? RAG.high.bg : surface.card)};
  color: ${({ $on }) => ($on ? RAG.high.fg : ink.soft)};
  font: inherit;
  font-size: ${font.small};
  font-weight: 600;
  cursor: pointer;
  transition: ${transition};

  &:hover { border-color: ${({ $on }) => ($on ? RAG.high.fg : ink.mute)}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.secondary}; outline-offset: 2px; }
`

const Showing = styled.span`
  margin-left: auto;
  font-size: ${font.caption};
  color: ${ink.mute};
  white-space: nowrap;
`

const Ring = styled.div`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: ${radius.round};
  background: ${({ $colour, $deg }) => `conic-gradient(${$colour} ${$deg}deg, ${line.base} 0)`};
  display: flex;
  align-items: center;
  justify-content: center;
`

const RingInner = styled.div`
  width: 34px;
  height: 34px;
  border-radius: ${radius.round};
  background: ${surface.card};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  color: ${ink.base};
  font-variant-numeric: tabular-nums;
`

const Warning = styled.span`
  font-size: ${font.caption};
  font-weight: 600;
  color: ${RAG.critical.fg};
`

const Calm = styled.span`
  font-size: ${font.caption};
  color: ${ink.mute};
`

const MetaDim = styled.span`
  font-size: ${font.caption};
  color: ${ink.soft};
`

const Pills = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  flex-basis: 100%;
`

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.5;
  background: ${({ $tone }) => (RAG[$tone] || RAG.low).bg};
  color: ${({ $tone }) => (RAG[$tone] || RAG.low).fg};
  white-space: nowrap;
`

const LinkPill = styled.button`
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  padding: 0 12px;
  border: 0;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  background: ${({ $tone }) => (RAG[$tone] || RAG.low).bg};
  color: ${({ $tone }) => (RAG[$tone] || RAG.low).fg};
  &:hover { text-decoration: underline; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`
